import { Button, Grid, List, ListItem } from '@material-ui/core';
import { selectIsClient } from 'features/user/userSlice';
import _ from 'lodash';
import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import CustomInput from '../../../components/CustomInput';
import DropZoneInDialog from '../../../components/DropZoneInDialog';
import ImportResultsDialog from '../../../components/ImportResultsDialog/ImportResultsDialog';
import NotificationProvider from "../../../components/NotificationProvider";
import { activityStatusesList } from '../activityConsts';
import { useStyles } from "../ActivityReportPageStyles";
import { useGetAllActivityItemsQuery, useGetImportTemplateMutation, useImportActivitiesMutation, useGetAllActivityItemMaterialsQuery } from '../activitySlice';
import { userHasOneClientAccount } from '../../user/userSlice';
import { useGetClientsQuery } from 'features/clients/clientSlice';

function ImportActivities({ refetch }) {
    const classes = useStyles();

    let { data: clients, error: clientsError, isLoading: isLoadingClients } = useGetClientsQuery();
    let { data: activityItems, error: activityItemsError, isFetching: isLoadingActivityItems } = useGetAllActivityItemsQuery();
    let { data: allMaterials, error: materialsError, isLoading: isLoadingMaterials } = useGetAllActivityItemMaterialsQuery();
    let [importActivities, { data: importedData, error: importError, isLoading: importLoading }] = useImportActivitiesMutation();
    const [importTemplate, { data: importActivitiesTemplate, error: templateError, isLoading: templateLoading }] = useGetImportTemplateMutation();

    const [showResults, setShowResults] = useState(false);
    const hasOneAccount = useSelector(userHasOneClientAccount);
    const isClient = useSelector(selectIsClient);

    let clientAccounts = _.flatten(clients?.map(c => c.clientAccounts));
    const [selectedClient, setSelectedClient] = useState();

    useEffect(() => {
        setSelectedClient(clientAccounts?.[0]?.id);
    }, [clients])

    const downloadTemplate = async () => {
        await importTemplate();
    }

    const onSave = async (file) => {
        let fd = new FormData();
        fd.append("uploaded_file", file);
        fd.append("clientAccountId", selectedClient ?? clientAccounts?.[0]?.id);

        const res = await importActivities(fd);
        if (res.error) {
            NotificationProvider.error("Failed to import activities");
            NotificationProvider.error(res.error?.data?.[0]);
        } else {
            if (res?.data != []) {
                setShowResults(true);
            }
        }
    }

    const materialsByActivity = useMemo(() => {
        return activityItems?.reduce((acc, item) => {
            const itemMaterials = allMaterials?.filter(m => m.activityItemId === item.id);

            if (itemMaterials?.length) {
                acc[item.id] = {
                    activityName: item.name,
                    activityId: item.id,
                    materials: itemMaterials
                };
            }

            return acc;
        }, {}) || {};
    }, [activityItems, allMaterials]);

    return (
        <>
            <DropZoneInDialog
                labelDialog={'Import Activities'}
                buttonLabel={'Import Activities'}
                actionButtonLabel={'Save'}
                onSave={onSave}
                valid={!!selectedClient || hasOneAccount}
                maxWidth={false}
                PaperProps={{
                    className: classes.importDialogPaper
                }}
                formContent={
                    <div className={classes.dialog}>
                        {clientAccounts?.length > 1 &&
                            <CustomInput
                                id="clientAccount"
                                label="Client"
                                value={selectedClient}
                                elementType="dropdown"
                                onChange={val => setSelectedClient(val)}
                                values={clientAccounts}
                                showEmpty
                                required
                            />
                        }
                        <div className={classes.downloadButtonWrapper}>
                            <Button
                                color="primary"
                                className={classes.downloadButton}
                                onClick={downloadTemplate}
                                disabled={templateLoading}
                            >
                                Download Template
                            </Button>
                        </div>
                        <br />
                        <Grid container direction='row' className={classes.activityIdLists} spacing={2}>
                            <Grid item xs={isClient ? 6 : 4} className={classes.box}>
                                <Grid item className={classes.directions}>
                                    Use the following codes to indicate the activity to add:
                                </Grid>
                                <List>
                                    {activityItems?.map((item, index) => (
                                        <ListItem key={index}>
                                            {item?.name} - {item?.id}
                                        </ListItem>
                                    ))}
                                </List>
                            </Grid>

                            {!isClient && <Grid item xs={4} className={classes.box}>
                                <Grid item className={classes.directions}>
                                    Use the following text to indicate the activity status to add:
                                </Grid>
                                <List>
                                    {activityStatusesList?.map((status, index) => (
                                        <ListItem key={index}>{status?.name}</ListItem>
                                    ))}
                                </List>
                            </Grid>}

                            <Grid item xs={isClient ? 6 : 4} className={classes.box}>
                                <Grid item className={classes.directions}>
                                    Use the following codes to indicate materials (optional):
                                </Grid>
                                <Grid item className={classes.materialNote}>
                                    Note: To add multiple materials to the same activity, create a separate row for each material, using the same activity details (VIN, Activity, Date, Time, Status).                                </Grid>
                                <List>
                                    {Object.values(materialsByActivity)?.map((activityGroup, actIndex) => (
                                        <div key={actIndex}>
                                            <ListItem className={classes.activityGroupHeader}>
                                                {activityGroup.activityName}
                                            </ListItem>
                                            {activityGroup.materials?.map((material, matIndex) => (
                                                <ListItem key={`${actIndex}-${matIndex}`} className={classes.materialItem}>
                                                    {material?.materialName}{material?.unitOfMeasure ? ` (${material.unitOfMeasure})` : ''} - {material?.id}
                                                </ListItem>
                                            ))}
                                        </div>
                                    ))}
                                </List>
                            </Grid>
                        </Grid>
                    </div>
                }
            />
            <ImportResultsDialog
                importedRows={importedData?.importedActivities}
                failedRows={importedData?.failedActivities}
                skippedRows={importedData?.skippedActivities}
                open={showResults || importLoading}
                setOpen={setShowResults}
                loading={importLoading}
                error={importError}
                reloadData={refetch}
                dataType="Activities"
                getDisplayFields={(item) => [
                    { label: 'Lot', value: item.activity?.lotName },
                    { label: 'VIN / Asset ID', value: item.activity?.descriptor },
                    { label: 'Activity', value: item.activity?.activityName }
                ]}
            />
        </>
    );
}

export default ImportActivities;